import { ReactNode } from 'react'
import InfoTag from './InfoTag'
import { cn } from '@/utilities/ui'

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

const fallbackTitleLevelMap: Record<HeadingLevel, HeadingLevel> = {
  h1: 'h2',
  h2: 'h3',
  h3: 'h4',
  h4: 'h5',
  h5: 'h6',
  h6: 'h6',
}

export interface SectionHeaderProps {
  infoTagText?: string | null
  title?: string | null
  subtitle?: string | null
  titleAs?: HeadingLevel
  infoTagAs?: HeadingLevel | 'div' | 'span'
  /**
   * Defines the semantic heading level this section should contribute.
   * Defaults to `h2`. When an info tag is present it uses this level,
   * otherwise the title uses it.
   */
  headingLevel?: HeadingLevel
  align?: 'left' | 'center' | 'right'
  /** Optional alignment override at the md breakpoint and up. */
  alignMd?: 'left' | 'center' | 'right'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  titleClassName?: string
  subtitleClassName?: string
  children?: ReactNode
}

export default function SectionHeader({
  infoTagText,
  title,
  subtitle,
  titleAs,
  infoTagAs,
  headingLevel = 'h2',
  align = 'left',
  alignMd,
  size = 'lg',
  className = '',
  titleClassName = '',
  subtitleClassName = '',
  children,
}: SectionHeaderProps) {
  // Normalize null/undefined to empty string or undefined
  const normalizedInfoTag = infoTagText ?? ''
  const normalizedTitle = title ?? ''
  const normalizedSubtitle = subtitle ?? undefined

  const alignClass =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'
  const alignMdClass = !alignMd
    ? ''
    : alignMd === 'center'
      ? 'md:text-center'
      : alignMd === 'right'
        ? 'md:text-right'
        : 'md:text-left'
  const subtitleAlignClass = align === 'center' ? 'mx-auto' : align === 'right' ? 'ml-auto' : ''
  const subtitleAlignMdClass = !alignMd
    ? ''
    : alignMd === 'center'
      ? 'md:mx-auto'
      : alignMd === 'right'
        ? 'md:ml-auto md:mr-0'
        : 'md:ml-0 md:mr-auto'
  const sizeClasses = size === 'sm' ? 'heading-sm' : size === 'md' ? 'heading-md' : 'heading-lg'
  const resolvedInfoTagAs = infoTagAs ?? (normalizedInfoTag ? headingLevel : ('span' as const))
  const resolvedTitleAs: HeadingLevel =
    titleAs ?? (normalizedInfoTag ? fallbackTitleLevelMap[headingLevel] : headingLevel)
  const TitleComponent = resolvedTitleAs

  return (
    <div className={cn('mb-6 md:mb-12', alignClass, alignMdClass, className)}>
      {normalizedInfoTag && (
        <div className="mb-3">
          <InfoTag as={resolvedInfoTagAs}>{normalizedInfoTag}</InfoTag>
        </div>
      )}

      {normalizedTitle && (
        <TitleComponent className={cn('text-primary', sizeClasses, titleClassName)}>
          {normalizedTitle}
        </TitleComponent>
      )}

      {normalizedSubtitle && (
        <p
          className={cn(
            'subtitle-lg text-secondary max-w-4xl pt-5',
            subtitleAlignClass,
            subtitleAlignMdClass,
            subtitleClassName,
          )}
        >
          {normalizedSubtitle}
        </p>
      )}

      {children}
    </div>
  )
}
